// src/events/EventsDashboard.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; // ⟵ added
import Logo from "../assets/events-logo.png";
import { db } from "../firebase";
import {
  collection, getDocs, query, where, limit,
} from "firebase/firestore";
import { FiUser, FiHash, FiX } from "react-icons/fi";
import { FaCalendarAlt, FaHotel, FaQrcode } from "react-icons/fa";

// Profile component
import OrganiserProfile from "./organiser_profile";

/* ────────────────────────────────────────────────────────── */
/* Brand / Theme                                              */
/* ────────────────────────────────────────────────────────── */
const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  page: "#f5f6f8",
  card: "#ffffff",
  headerBg: "#1f2937",
};

const COLL_SIGNUPS = "organiser_signup";

/* ────────────────────────────────────────────────────────── */
/* Data helpers                                               */
/* ────────────────────────────────────────────────────────── */
async function findSignupByMobile(mobile) {
  const col = collection(db, COLL_SIGNUPS);

  // Primary: nested field
  let snap = await getDocs(query(col, where("general.contact.mobile", "==", mobile), limit(1)));
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }

  // Fallback: early saves
  snap = await getDocs(query(col, where("mobileVerified", "==", mobile), limit(1)));
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }

  return null;
}

function pickContactName(signup) {
  if (!signup) return "—";
  const g = signup.general || {};
  const c = g.contact || {};
  return c.name || c.fullName || signup.contactName || signup.name || "—";
}

function pickOrganiserUID(signup) {
  if (!signup) return "—";
  return signup.organiser_UID || signup.organiserUID || signup.uid || signup.orgUID || "—";
}

function getSessionMobile() {
  try {
    const raw = localStorage.getItem("events_session"); // set by setEventsSession({ mobile })
    const j = raw ? JSON.parse(raw) : null;
    const m = j?.mobile || j?.phone || "";
    return String(m).replace(/\D/g, "").slice(-10);
  } catch {
    return "";
  }
}

/* ────────────────────────────────────────────────────────── */
/* Page                                                       */
/* ────────────────────────────────────────────────────────── */
export default function EventsDashboard() {
  const nav = useNavigate(); // ⟵ added

  const [contactName, setContactName] = useState("—");
  const [organiserUID, setOrganiserUID] = useState("—");
  const [loading, setLoading] = useState(true);

  // profile drawer state
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const mobile = getSessionMobile();
      if (!mobile) {
        setLoading(false);
        return;
      }
      try {
        const signup = await findSignupByMobile(mobile);
        if (!mounted) return;
        setContactName(pickContactName(signup));
        setOrganiserUID(pickOrganiserUID(signup));
      } catch (e) {
        console.warn("Signup fetch failed:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // close on ESC
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setProfileOpen(false);
    }
    if (profileOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [profileOpen]);

  return (
    <div style={{ minHeight: "100vh", background: COLORS.page, display: "grid", gridTemplateRows: "72px 1fr" }}>
      {/* Header */}
      <header
        style={{
          background: COLORS.headerBg,
          color: "#fff",
          display: "grid",
          gridTemplateColumns: "220px 1fr auto 56px",
          alignItems: "center",
          gap: 16,
          padding: "0 16px",
          borderBottom: `1px solid rgba(255,255,255,0.08)`,
        }}
      >
        {/* Left: Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={Logo} alt="Events" style={{ height: 36 }} />
        </div>

        {/* Center: Title */}
        <div style={{ textAlign: "center", fontWeight: 900, fontSize: 32, letterSpacing: 0.2 }}>
          Host Your Events
        </div>

        {/* Right: two lines */}
        <div style={{ display: "grid", gap: 4, justifyItems: "end" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
            <FiUser />
            <span>{loading ? "Loading…" : `Hi, ${contactName}`}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
            <FiHash />
            <span>{loading ? "—" : organiserUID}</span>
          </div>
        </div>

        {/* Far Right: user avatar (click to open profile) */}
        <div
          title="Open profile"
          onClick={() => setProfileOpen(true)}
          style={{
            width: 40, height: 40,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
            display: "grid", placeItems: "center",
            border: "1px solid rgba(255,255,255,0.25)",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <FiUser />
        </div>
      </header>

      {/* Main (center the cards lower on page) */}
      <main
        style={{
          display: "grid",
          placeItems: "center",
          padding: 24,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 1100,
            marginTop: 64, // lower on page
            display: "grid",
            gap: 24,
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            alignItems: "stretch",
          }}
        >
          <DashCard
            icon={<FaCalendarAlt size={48} />}
            title="Manage Events"
            subtitle="Dashboard where you can manage and host your event"
            onClick={() => nav("/events/manage")} // ⟵ linked
          />

          <DashCard
            icon={<FaHotel size={48} />}
            title="Manage Venues"
            subtitle="A dashboard where you can add and manage your venues"
            onClick={() => nav("/events/venues")} // ⟵ linked
          />

          <DashCard
            icon={<FaQrcode size={48} />}
            title="Scan Tickets"
            subtitle="An app to scan and validate visitor tickets"
            onClick={() => alert("Scan Tickets — coming soon")} // ⟵ coming soon
          />
        </div>
      </main>

      {/* ───────────────────────────────────────────────────── */}
      {/* PROFILE DRAWER                                       */}
      {/* ───────────────────────────────────────────────────── */}
      {profileOpen && (
        <div style={drawerBackdrop} onClick={() => setProfileOpen(false)}>
          {/* stop clicks inside panel from closing */}
          <div
            style={{
              ...drawerPanel,
              transform: "translateX(0)", // slide-in
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={drawerHeader}>
              <div style={{ fontWeight: 900 }}>Profile</div>
              <button
                type="button"
                onClick={() => setProfileOpen(false)}
                style={drawerCloseBtn}
                aria-label="Close profile panel"
              >
                <FiX />
              </button>
            </div>

            <div style={drawerBody}>
              {/* Render your organiser profile inside */}
              <OrganiserProfile organiserUID={organiserUID} contactName={contactName} />
            </div>
          </div>

          {/* off-screen panel for smooth transition when mounting */}
          <div
            style={{
              ...drawerPanel,
              transform: "translateX(100%)", // ⟵ hidden off to the right
              pointerEvents: "none",
              visibility: "hidden",
            }}
          />
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/* Parts                                                      */
/* ────────────────────────────────────────────────────────── */
function DashCard({ icon, title, subtitle, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        background: COLORS.card,
        borderRadius: 16,
        border: `1px solid ${COLORS.border}`,
        boxShadow: "0 14px 28px rgba(0,0,0,.08)",
        padding: 24,
        minHeight: 260,
        cursor: "pointer",
        transition: "transform .12s ease, box-shadow .12s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 18px 34px rgba(242,12,83,.20)")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 14px 28px rgba(0,0,0,.08)")}
    >
      <div
        style={{
          width: 84, height: 84,
          display: "grid", placeItems: "center",
          borderRadius: 18,
          border: `1px solid ${COLORS.border}`,
          background: "#fff",
          color: COLORS.primary,
          boxShadow: "0 8px 22px rgba(242,12,83,.18)",
          marginBottom: 14,
        }}
      >
        {icon}
      </div>

      <h3 style={{ margin: "6px 0 6px", fontSize: 20, color: COLORS.text, fontWeight: 800 }}>
        {title}
      </h3>

      <p style={{ margin: 0, color: COLORS.subtext, lineHeight: 1.6, maxWidth: 340 }}>
        {subtitle}
      </p>
    </button>
  );
}

/* ────────────────────────────────────────────────────────── */
/* STYLES: Drawer                                             */
/* ────────────────────────────────────────────────────────── */
const drawerBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  zIndex: 80,
  display: "block",
};

const drawerPanelBase = {
  position: "fixed",
  top: 0,
  right: 0,                   // anchor to right
  height: "100vh",
  width: "50vw",              // covers 50% of the screen from the right
  maxWidth: 720,
  background: "#fff",
  borderLeft: `1px solid ${COLORS.border}`,   // border on the left side
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
  display: "flex",
  flexDirection: "column",
  transition: "transform .22s ease-out",
};

const drawerPanel = {
  ...drawerPanelBase,
};

const drawerHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 12,
  borderBottom: `1px solid ${COLORS.border}`,
};

const drawerCloseBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 36,
  width: 36,
  borderRadius: 8,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};

const drawerBody = {
  padding: 12,
  overflowY: "auto",
  flex: 1,
};
